import { type Session } from "@decentriq/core";
import { useCallback, useMemo, useRef } from "react";
import { useApiCore } from "contexts";

interface ScopeIdHookPayload {
  dataRoomId: string;
  session?: Session;
}

interface ScopeIdHookResult {
  getScopeId: () => Promise<string>;
  resetScopeId: (scopeId?: string | null) => void;
}

const useScopeId = ({
  session,
  dataRoomId,
}: ScopeIdHookPayload): ScopeIdHookResult => {
  const { client } = useApiCore();
  const scopeIdRef = useRef<string | null>(null);
  const getScopeId = useCallback(async () => {
    if (!session) {
      throw new Error("Session is not available");
    }
    if (scopeIdRef.current) {
      return scopeIdRef.current;
    }
    const scopeId = await client.ensureDcrDataScope(dataRoomId);
    scopeIdRef.current = scopeId;
    return scopeId;
  }, [session, client, dataRoomId]);
  const resetScopeId = useCallback((scopeId?: string | null) => {
    scopeIdRef.current = scopeId ?? null;
  }, []);
  return useMemo(
    () => ({ getScopeId, resetScopeId }),
    [getScopeId, resetScopeId]
  );
};

export default useScopeId;
